import React, { useMemo } from 'react';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { parseCurrency, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS SVG PREMIUM ---
const IconShield = () => (
    <svg className="w-5 h-5 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const IconBuilding = () => (
    <svg className="w-5 h-5 text-indigo-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m3 0h1m-1-4h.01M9 16h.01M15 16h.01M9 12h.01M15 12h.01M9 8h.01M15 8h.01" />
    </svg>
);

/**
 * KpisRenovaciones - Dashboard estadístico macro de la cartera de vencimientos.
 * Actualizado: Tablas comparativas lado a lado (Por Riesgo y Por Compañía).
 */
const KpisRenovaciones = ({ registros, isDbLoaded }) => {

    const metrics = useMemo(() => {
        if (!isDbLoaded || !registros || registros.length === 0) {
            return {
                cantidadTotalARenovar: 0,
                cantidadRenovadas: 0,
                cantidadNoRenueva: 0,
                cantidadSinGestion: 0,
                cantidadAnuladas: 0,
                cantidadAnuladasEntrantes: 0,
                cantidadAnuladasSalientes: 0,
                renovadaARS: 0,
                renovadaUSD: 0,
                porRiesgo: [],
                porCompania: []
            };
        }

        let cantidadTotalARenovar = 0;
        let cantidadRenovadas = 0;
        let cantidadNoRenueva = 0;
        let cantidadSinGestion = 0;
        let cantidadAnuladasEntrantes = 0;
        let cantidadAnuladasSalientes = 0;

        let renovadaARS = 0;
        let renovadaUSD = 0;
        const riesgosMap = {};
        const companiasMap = {};

        registros.forEach(item => {
            const estadoEmisionOrig = String(item.estadoEmision || '').trim().toUpperCase();
            const estadoGestion = String(item.estadoGestionRenovacion || '').trim().toUpperCase();
            const tipoRenovacion = String(item.tipoRenovacion || item.formaRenovacion || '').trim().toUpperCase();

            // 1. Evaluar Anulación Entrante (Cartera previa)
            const esAnuladaEmision = estadoEmisionOrig === 'ANULADA' || estadoEmisionOrig === 'CANCELADA';

            if (esAnuladaEmision) {
                cantidadAnuladasEntrantes += 1;
            } else {
                cantidadTotalARenovar += 1;

                // 2. Evaluar Anulación Saliente (Póliza Nueva)
                const pNueva = item.polizaNuevaObj || {};
                const estadoEmisionNueva = String(item.estadoEmisionNueva || pNueva.estadoEmision || pNueva.estadoEmisionCalculado || '').trim().toUpperCase();
                const esAnuladaNueva = estadoEmisionNueva === 'ANULADA' || estadoEmisionNueva === 'CANCELADA';

                // 3. Clasificación Jerárquica y Exclusiva (Suma 100% Exacta)
                const esRenovadaActiva = estadoGestion === 'RENOVADA' && !esAnuladaNueva;
                const esNoRenueva = estadoGestion === 'NO RENUEVA' || estadoGestion === 'NO_RENOVO' || tipoRenovacion === 'NO RENOVABLE';

                if (esRenovadaActiva) {
                    cantidadRenovadas += 1;

                    const rawPrima = item.primaEmitidaNueva !== undefined ? item.primaEmitidaNueva : (item.polizaNuevaObj?.primaEmitida || item.primaEmitida || 0);
                    const prima = Math.abs(parseCurrency(rawPrima));
                    const monedaRaw = String(item.monedaNueva || item.polizaNuevaObj?.moneda || item.moneda || '$').toUpperCase().trim();
                    const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                    if (esUSD) renovadaUSD += prima;
                    else renovadaARS += prima;

                    // Agrupación por Riesgo / Rama
                    const riesgoNombre = item.riesgo || 'SIN ESPECIFICAR';
                    if (!riesgosMap[riesgoNombre]) {
                        riesgosMap[riesgoNombre] = { nombre: riesgoNombre, ARS: 0, USD: 0, cant: 0 };
                    }
                    riesgosMap[riesgoNombre].cant += 1;
                    if (esUSD) riesgosMap[riesgoNombre].USD += prima;
                    else riesgosMap[riesgoNombre].ARS += prima;

                    // Agrupación por Compañía Receptora/Emisora
                    const companiaNombre = item.companiaNueva || item.polizaNuevaObj?.compania || item.compania || 'SIN ESPECIFICAR';
                    if (!companiasMap[companiaNombre]) {
                        companiasMap[companiaNombre] = { nombre: companiaNombre, ARS: 0, USD: 0, cant: 0 };
                    }
                    companiasMap[companiaNombre].cant += 1;
                    if (esUSD) companiasMap[companiaNombre].USD += prima;
                    else companiasMap[companiaNombre].ARS += prima;

                } else if (esAnuladaNueva) {
                    cantidadAnuladasSalientes += 1;
                } else if (esNoRenueva) {
                    cantidadNoRenueva += 1;
                } else {
                    cantidadSinGestion += 1;
                }
            }
        });

        return {
            cantidadTotalARenovar,
            cantidadRenovadas,
            cantidadNoRenueva,
            cantidadSinGestion,
            cantidadAnuladas: cantidadAnuladasEntrantes + cantidadAnuladasSalientes,
            cantidadAnuladasEntrantes,
            cantidadAnuladasSalientes,
            renovadaARS,
            renovadaUSD,
            porRiesgo: Object.values(riesgosMap).sort((a, b) => b.cant - a.cant),
            porCompania: Object.values(companiasMap).sort((a, b) => b.cant - a.cant)
        };
    }, [registros, isDbLoaded]);

    if (!isDbLoaded) {
        return (
            <div className="p-8 text-center text-slate-400 italic text-sm select-none">
                Cargando estadísticas de renovaciones...
            </div>
        );
    }

    if (registros.length === 0 || !metrics) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-2xl border border-slate-200 select-none">
                No se encontraron pólizas en este período de consulta.
            </div>
        );
    }

    return (
        <div className="space-y-8 text-left animate-fade-in">
            
            {/* CARDS SUPERIORES DE TOTALES DE RENOVACIÓN */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                
                {/* 1. PÓLIZAS A RENOVAR */}
                <div className="bg-white p-4 border border-slate-200 rounded-2xl shadow-sm flex flex-col justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Pólizas a Renovar</p>
                        <h4 className="text-2xl font-black text-slate-800 mt-2 font-mono">
                            {metrics.cantidadTotalARenovar}
                        </h4>
                    </div>
                    <p className="text-[9px] text-slate-400 mt-2 font-bold uppercase tracking-wider">Excluye anuladas previas</p>
                </div>

                {/* 2. PÓLIZAS RENOVADAS */}
                <div className="bg-white p-4 border border-slate-200 rounded-2xl shadow-sm flex flex-col justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest leading-none">Pólizas Renovadas</p>
                        <h4 className="text-2xl font-black text-indigo-600 mt-2 font-mono">
                            {metrics.cantidadRenovadas}
                        </h4>
                    </div>
                    <p className="text-[9px] text-indigo-500 mt-2 font-bold uppercase tracking-wider">Efectivamente renovadas</p>
                </div>

                {/* 3. PÓLIZAS NO RENUEVAN */}
                <div className="bg-white p-4 border border-rose-200 rounded-2xl shadow-sm flex flex-col justify-between transition-all hover:shadow-md bg-rose-50/20">
                    <div>
                        <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest leading-none">No Renuevan</p>
                        <h4 className="text-2xl font-black text-rose-600 mt-2 font-mono">
                            {metrics.cantidadNoRenueva}
                        </h4>
                    </div>
                    <p className="text-[9px] text-rose-500 mt-2 font-bold uppercase tracking-wider">Pérdida de cartera</p>
                </div>

                {/* 4. SIN GESTIÓN */}
                <div className="bg-white p-4 border border-slate-200 rounded-2xl shadow-sm flex flex-col justify-between transition-all hover:shadow-md bg-slate-50/30">
                    <div>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">Sin Gestión</p>
                        <h4 className="text-2xl font-black text-slate-600 mt-2 font-mono">
                            {metrics.cantidadSinGestion}
                        </h4>
                    </div>
                    <p className="text-[9px] text-slate-400 mt-2 font-bold uppercase tracking-wider">Pendiente de clasificar</p>
                </div>

                {/* 5. PÓLIZAS ANULADAS (DESGLOSADAS) */}
                <div className="bg-white p-4 border border-slate-200 rounded-2xl shadow-sm flex flex-col justify-between transition-all hover:shadow-md bg-slate-50/70">
                    <div>
                        <div className="flex justify-between items-center">
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">Anuladas</p>
                            <span className="text-[9px] font-mono font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                                Total: {metrics.cantidadAnuladas}
                            </span>
                        </div>
                        <h4 className="text-2xl font-black text-slate-700 mt-2 font-mono">
                            {metrics.cantidadAnuladas}
                        </h4>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-200/60 text-[9px] font-bold uppercase tracking-tight space-y-0.5">
                        <div className="flex justify-between text-slate-500">
                            <span>Previas (Entrante):</span>
                            <span className="font-mono font-black text-slate-700">{metrics.cantidadAnuladasEntrantes}</span>
                        </div>
                        <div className="flex justify-between text-rose-600">
                            <span>Caídas (Saliente):</span>
                            <span className="font-mono font-black text-rose-700">{metrics.cantidadAnuladasSalientes}</span>
                        </div>
                    </div>
                </div>

                {/* 6. PRIMA EMITIDA ARS */}
                <div className="bg-white p-4 border border-emerald-100 rounded-2xl shadow-sm flex flex-col justify-between transition-all hover:shadow-md bg-emerald-50/10">
                    <div>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest leading-none">Prima Emitida (ARS)</p>
                        <h4 className="text-lg font-black text-emerald-600 mt-2 font-mono tracking-tight">
                            {formatCurrencyUniversal(metrics.renovadaARS, '$')}
                        </h4>
                    </div>
                    <p className="text-[9px] text-emerald-600/70 mt-2 font-bold uppercase tracking-wider">Emisión nueva activa ($)</p>
                </div>

                {/* 7. PRIMA EMITIDA USD */}
                <div className="bg-white p-4 border border-blue-100 rounded-2xl shadow-sm flex flex-col justify-between transition-all hover:shadow-md bg-blue-50/10">
                    <div>
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest leading-none">Prima Emitida (USD)</p>
                        <h4 className="text-lg font-black text-blue-600 mt-2 font-mono tracking-tight">
                            {formatCurrencyUniversal(metrics.renovadaUSD, 'U$S')}
                        </h4>
                    </div>
                    <p className="text-[9px] text-blue-600/70 mt-2 font-bold uppercase tracking-wider">Emisión nueva activa (U$S)</p>
                </div>

            </div>

            {/* CONTENEDOR DE TABLAS COMPARATIVAS (LADO A LADO) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full items-start">
                
                {/* TABLA 1: RENDIMIENTO POR RIESGO / RAMA */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-left flex flex-col w-full">
                    <div className="p-4 px-6 bg-slate-50 border-b font-black text-[11px] text-slate-500 uppercase tracking-wider flex items-center gap-2.5 select-none">
                        <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600">
                            <IconShield />
                        </div>
                        <div>
                            <span className="block font-black text-slate-800 leading-none">Renovaciones por Riesgo / Rama</span>
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-1 block">Casos renovados y primas por rama</span>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-xs border-collapse">
                            <thead className="bg-slate-50/50 text-slate-400 border-b uppercase text-[9px] font-black tracking-wider select-none">
                                <tr>
                                    <th className="p-3 pl-6 text-left">Riesgo / Rama</th>
                                    <th className="p-3 text-center">Casos</th>
                                    <th className="p-3 text-right">Prima (ARS)</th>
                                    <th className="p-3 text-right pr-6">Prima (USD)</th>
                                </tr>
                            </thead>
                            <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                                {metrics.porRiesgo.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center text-slate-400 italic">
                                            Sin renovaciones por riesgo en este período.
                                        </td>
                                    </tr>
                                ) : (
                                    metrics.porRiesgo.map(r => (
                                        <tr key={r.nombre} className="hover:bg-slate-50/40 transition-colors">
                                            <td className="p-3 pl-6 text-slate-900 font-black uppercase text-xs">
                                                {r.nombre}
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className="bg-blue-50 border border-blue-150 text-blue-700 px-2.5 py-0.5 rounded-full text-[10px] font-bold">
                                                    {r.cant}
                                                </span>
                                            </td>
                                            <td className="p-3 text-right font-mono text-emerald-600 text-xs font-black">
                                                {r.ARS > 0 ? formatCurrencyUniversal(r.ARS, '$ ') : '-'}
                                            </td>
                                            <td className="p-3 text-right font-mono text-blue-600 text-xs font-black pr-6">
                                                {r.USD > 0 ? formatCurrencyUniversal(r.USD, 'U$S ') : '-'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* TABLA 2: RENDIMIENTO POR COMPAÑÍA */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-left flex flex-col w-full">
                    <div className="p-4 px-6 bg-slate-50 border-b font-black text-[11px] text-slate-500 uppercase tracking-wider flex items-center gap-2.5 select-none">
                        <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600">
                            <IconBuilding />
                        </div>
                        <div>
                            <span className="block font-black text-slate-800 leading-none">Renovaciones por Compañía</span>
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-1 block">Casos renovados y primas por aseguradora emisora</span>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-xs border-collapse">
                            <thead className="bg-slate-50/50 text-slate-400 border-b uppercase text-[9px] font-black tracking-wider select-none">
                                <tr>
                                    <th className="p-3 pl-6 text-left">Compañía</th>
                                    <th className="p-3 text-center">Casos</th>
                                    <th className="p-3 text-right">Prima (ARS)</th>
                                    <th className="p-3 text-right pr-6">Prima (USD)</th>
                                </tr>
                            </thead>
                            <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                                {metrics.porCompania.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center text-slate-400 italic">
                                            Sin renovaciones por compañía en este período.
                                        </td>
                                    </tr>
                                ) : (
                                    metrics.porCompania.map(c => (
                                        <tr key={c.nombre} className="hover:bg-slate-50/40 transition-colors">
                                            <td className="p-3 pl-6 text-slate-900 font-black uppercase text-xs">
                                                {c.nombre}
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className="bg-indigo-50 border border-indigo-150 text-indigo-700 px-2.5 py-0.5 rounded-full text-[10px] font-bold">
                                                    {c.cant}
                                                </span>
                                            </td>
                                            <td className="p-3 text-right font-mono text-emerald-600 text-xs font-black">
                                                {c.ARS > 0 ? formatCurrencyUniversal(c.ARS, '$ ') : '-'}
                                            </td>
                                            <td className="p-3 text-right font-mono text-blue-600 text-xs font-black pr-6">
                                                {c.USD > 0 ? formatCurrencyUniversal(c.USD, 'U$S ') : '-'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default KpisRenovaciones;